import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, Plus, X } from 'lucide-react';

const MIN_SELECTION = 2;
const MAX_SELECTION = 4;

const LikieeSelectionModal = ({
  projects,
  selectedProject,
  onProjectChange,
  likiees,
  initialSelection,
  onClose,
  onNext,
  isLoading,
  error,
}) => {
  const { t } = useTranslation();
  const [selection, setSelection] = useState(initialSelection || []);
  const [viewMode, setViewMode] = useState('projects');

  useEffect(() => {
    // Only reset selection when initialSelection changes (e.g., when modal opens)
    // Don't reset when likiees change (when switching projects)
    if (initialSelection) {
      setSelection(initialSelection);
    }
  }, [initialSelection]);

  const selectedCount = selection.length;

  const handleToggle = likiee => {
    if (likiee.isCurrent) return;

    const exists = selection.find(item => item.id === likiee.id);
    if (exists) {
      setSelection(prev => prev.filter(item => item.id !== likiee.id));
      return;
    }

    if (selection.length >= MAX_SELECTION) return;

    setSelection(prev => [...prev, likiee]);
  };

  const handleRemoveSelection = likieeId => {
    setSelection(prev => prev.filter(item => item.id !== likieeId));
  };

  const isDisabled = likiee => {
    if (likiee.isCurrent) return false;
    const exists = selection.find(item => item.id === likiee.id);
    if (exists) return false;
    return selection.length >= MAX_SELECTION;
  };

  const handleSubmit = () => {
    if (selection.length < MIN_SELECTION) return;
    onNext(selection);
  };

  const handleProjectSelect = project => {
    onProjectChange(project);
    setViewMode('likiees');
  };

  const handleBackToProjects = () => {
    setViewMode('projects');
  };

  const renderProjectIcon = project => {
    if (!project?.icon) {
      const initials = project?.name
        ?.split(' ')
        .map(word => word[0])
        .join('')
        .substring(0, 2)
        .toUpperCase();
      return <div className='project-card__icon project-card__icon--initials'>{initials || '??'}</div>;
    }

    if (typeof project.icon === 'string') {
      return <img src={project.icon} alt='' className='project-card__icon' />;
    }

    const Icon = project.icon;
    return (
      <div className='project-card__icon project-card__icon--svg'>
        <Icon />
      </div>
    );
  };

  const renderSelectionBoxes = () => {
    const boxes = [];
    for (let i = 0; i < MAX_SELECTION; i++) {
      const selectedLikiee = selection[i];
      boxes.push(
        <div key={i} className={`selection-box ${selectedLikiee ? 'is-filled' : 'is-empty'}`}>
          {selectedLikiee ? (
            <>
              <button
                type='button'
                className='selection-box__remove'
                onClick={() => handleRemoveSelection(selectedLikiee.id)}
                aria-label={t('ai_compare.remove')}
              >
                <X size={14} />
              </button>
              <img src={selectedLikiee.image} alt={selectedLikiee.name} className='selection-box__image' />
              {selectedLikiee.isCurrent && (
                <span className='selection-box__badge'>{t('ai_compare.current_label')}</span>
              )}
            </>
          ) : (
            <div className='selection-box__placeholder'>
              <Plus size={24} />
            </div>
          )}
        </div>,
      );
    }
    return boxes;
  };

  return (
    <div className='ai-compare-modal-overlay' role='dialog' aria-modal='true'>
      <div className='ai-compare-modal ai-compare-modal--selection'>
        <button type='button' className='ai-compare-modal__close' onClick={onClose} aria-label={t('ai_compare.close')}>
          ×
        </button>

        <div className='modal-header-section'>
          <h2 className='modal-title'>{t('ai_compare.compare_products')}</h2>
          <p className='modal-subtitle'>{t('ai_compare.select_project_subtitle')}</p>
        </div>

        {/* Selection Boxes */}
        <div className='selection-boxes'>
          <div className='selection-boxes__label'>
            {selectedCount} {t('ai_compare.selected_label')}
          </div>
          <div className='selection-boxes__grid'>{renderSelectionBoxes()}</div>
        </div>

        {/* Projects/Likiees Navigation */}
        {viewMode === 'likiees' && (
          <div className='navigation-header'>
            <button type='button' className='back-to-projects' onClick={handleBackToProjects}>
              <ChevronLeft size={18} />
              <span>{t('ai_compare.back_to_projects')}</span>
            </button>
            <span className='current-project-label'>{selectedProject?.name}</span>
          </div>
        )}

        {/* Content Area */}
        <div className='selection-content'>
          {viewMode === 'projects' ? (
            <div className='projects-grid-compact'>
              {projects.map(project => (
                <button
                  key={project.id}
                  type='button'
                  className='project-card-compact'
                  onClick={() => handleProjectSelect(project)}
                >
                  {renderProjectIcon(project)}
                  <div className='project-card-compact__content'>
                    <span className='project-card-compact__name'>{project.name}</span>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className='likiee-selection-area'>
              {isLoading && <div className='ai-compare-loading'>{t('ai_compare.loading')}</div>}
              {error && !isLoading && <div className='ai-compare-error'>{error}</div>}

              {!isLoading && !error && (
                <div className='likiee-grid-compact' role='list'>
                  {likiees.map(likiee => {
                    const isSelected = selection.some(item => item.id === likiee.id);
                    const disabled = isDisabled(likiee);
                    return (
                      <button
                        key={likiee.id}
                        type='button'
                        className={`likiee-card-compact ${isSelected ? 'is-selected' : ''} ${
                          disabled ? 'is-disabled' : ''
                        }`}
                        onClick={() => handleToggle(likiee)}
                        disabled={likiee.isCurrent}
                      >
                        {likiee.image ? (
                          <img src={likiee.image} alt='' className='likiee-card-compact__image' />
                        ) : (
                          <div className='likiee-card-compact__image likiee-card-compact__image--placeholder' />
                        )}
                        <div className='likiee-card-compact__info'>
                          <span className='likiee-card-compact__name'>
                            {likiee.name}
                            {likiee.isCurrent && (
                              <span className='badge-compact badge-compact--current'>
                                {t('ai_compare.current_label')}
                              </span>
                            )}
                          </span>
                          {likiee.displayPrice && (
                            <span className='likiee-card-compact__price'>{likiee.displayPrice}</span>
                          )}
                        </div>
                        {isSelected && (
                          <div className='likiee-card-compact__check'>
                            <svg width='16' height='16' viewBox='0 0 16 16' fill='none'>
                              <path
                                d='M13.3333 4L6 11.3333L2.66667 8'
                                stroke='currentColor'
                                strokeWidth='2'
                                strokeLinecap='round'
                                strokeLinejoin='round'
                              />
                            </svg>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        <div className='modal-footer'>
          <button type='button' className='button-secondary' onClick={onClose}>
            {t('ai_compare.cancel')}
          </button>
          <button
            type='button'
            className='button-primary'
            onClick={handleSubmit}
            disabled={selection.length < MIN_SELECTION}
          >
            {t('ai_compare.next')}
          </button>
        </div>
      </div>
    </div>
  );
};

LikieeSelectionModal.propTypes = {
  projects: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      icon: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
      itemsCount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
  ),
  selectedProject: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }),
  onProjectChange: PropTypes.func.isRequired,
  likiees: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      image: PropTypes.string,
      displayPrice: PropTypes.string,
      isCurrent: PropTypes.bool,
    }),
  ),
  initialSelection: PropTypes.array,
  onClose: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  error: PropTypes.string,
};

LikieeSelectionModal.defaultProps = {
  projects: [],
  likiees: [],
  initialSelection: [],
  selectedProject: null,
  isLoading: false,
  error: '',
};

export default LikieeSelectionModal;
